import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { AssetWithType, AssetType, CreateAssetRequest } from '../types';
import { formatINR } from '../utils/currency';

interface AssetListProps {
  onAssetSelect?: (asset: AssetWithType) => void;
}

const AssetList: React.FC<AssetListProps> = ({ onAssetSelect }) => {
  const [assets, setAssets] = useState<AssetWithType[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<AssetWithType | null>(null);
  const [formData, setFormData] = useState<CreateAssetRequest>({
    symbol: '',
    name: '',
    asset_type_id: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assetsData, typesData] = await Promise.all([
        apiClient.getAssets(),
        apiClient.getAssetTypes(),
      ]);
      setAssets(assetsData);
      setAssetTypes(typesData);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await apiClient.updateAsset(editingAsset.id, formData);
        setEditingAsset(null);
      } else {
        await apiClient.createAsset(formData);
      }
      setShowCreateForm(false);
      setFormData({ symbol: '', name: '', asset_type_id: 0 });
      fetchData();
    } catch (err) {
      console.error('Error saving asset:', err);
      alert('Failed to save asset');
    }
  };

  const handleEdit = (asset: AssetWithType) => {
    setEditingAsset(asset);
    setFormData({
      symbol: asset.symbol,
      name: asset.name,
      asset_type_id: asset.asset_type_id,
      current_price: asset.current_price ? parseFloat(asset.current_price) : undefined,
      notes: asset.notes || undefined,
    });
    setShowCreateForm(true);
  };

  const handleCancelEdit = () => {
    setEditingAsset(null);
    setShowCreateForm(false);
    setFormData({ symbol: '', name: '', asset_type_id: 0 });
  };

  const handleRefreshPrice = async (assetId: number) => {
    try {
      await apiClient.refreshAssetPrice(assetId);
      fetchData();
    } catch (err) {
      console.error('Error refreshing price:', err);
      alert('Failed to refresh price');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-6 animate-pulse">
            <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-white/10 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Add Asset button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">My Assets</h2>
        <button
          onClick={() => {
            if (showCreateForm) {
              handleCancelEdit();
            } else {
              setShowCreateForm(true);
            }
          }}
          className="px-4 py-2 bg-[var(--accent-gold)] hover:bg-[var(--accent-gold)]/80 rounded-lg font-medium transition-colors"
        >
          {showCreateForm ? 'Cancel' : '+ Add Asset'}
        </button>
      </div>

      {/* Create Asset Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateAsset} className="rounded-xl border border-white/10 bg-white/5 p-6 space-y-4">
          <h3 className="text-lg font-semibold mb-4">{editingAsset ? 'Edit Asset' : 'Add New Asset'}</h3>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Asset Type</label>
            <select
              value={formData.asset_type_id}
              onChange={(e) => setFormData({ ...formData, asset_type_id: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
              required
            >
              <option value={0}>Select asset type...</option>
              {assetTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} ({type.category})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Symbol</label>
            <input
              type="text"
              value={formData.symbol}
              onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
              placeholder="e.g., RELIANCE.NS, AAPL"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Reliance Industries, Apple Inc"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Current Price (Optional)</label>
            <input
              type="number"
              step="0.01"
              value={formData.current_price || ''}
              onChange={(e) => setFormData({ ...formData, current_price: parseFloat(e.target.value) || undefined })}
              placeholder="₹0.00"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Notes (Optional)</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value || undefined })}
              placeholder="Add notes about this asset..."
              rows={2}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[var(--accent-gold)] resize-none"
            />
          </div>

          <button
            type="submit"
            className="w-full px-4 py-2 bg-[var(--accent-gold)] hover:bg-[var(--accent-gold)]/80 rounded-lg font-medium transition-colors"
          >
            {editingAsset ? 'Update Asset' : 'Create Asset'}
          </button>
        </form>
      )}

      {/* Assets Grid */}
      {assets.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center">
          <p className="text-slate-400 text-lg">No assets yet. Add your first asset to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map((asset) => {
            const currentPrice = asset.current_price ? parseFloat(asset.current_price) : 0;
            
            return (
              <div
                key={asset.id}
                className="rounded-xl border border-white/10 bg-white/5 p-6 hover:border-white/20 transition-all cursor-pointer"
                onClick={() => onAssetSelect && onAssetSelect(asset)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-lg">{asset.symbol}</h3>
                    <p className="text-sm text-slate-400">{asset.name}</p>
                  </div>
                  <span className="px-2 py-1 text-xs rounded bg-white/10 text-slate-300">
                    {asset.asset_type.name}
                  </span>
                </div>

                {currentPrice > 0 ? (
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-slate-400">Current Price</p>
                      <p className="text-xl font-bold text-white">{formatINR(currentPrice)}</p>
                    </div>
                    {asset.last_price_update && (
                      <p className="text-xs text-slate-500">
                        Updated: {new Date(asset.last_price_update).toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No price data</p>
                )}

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefreshPrice(asset.id);
                    }}
                    className="flex-1 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    🔄 Refresh
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(asset);
                    }}
                    className="flex-1 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    ✏️ Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssetList;